/**
 * Moving a plain calendar day in and out of `BasicDatePicker`.
 *
 * The picker holds a `Date` — an instant — and formats it in the reader's own timezone. A field
 * that means *a day* rather than a moment therefore cannot hand it a UTC midnight: west of
 * Greenwich, `2026-09-15` arrives as the 14th and is shown, and saved back, as the wrong day.
 *
 * So a day crosses the boundary as local midnight, which formats as itself everywhere, and comes
 * back read off the local parts. Nothing here touches UTC, which is the point: the string is a
 * date on a calendar and has no timezone to lose.
 */

const pad = (value: number) => String(value).padStart(2, '0');

/** `YYYY-MM-DD` to the `Date` the picker should show. Blank or unparseable gives `null`. */
export const toCalendarDate = (day: string): Date | null => {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim());
    if (!parts) {
        return null;
    }

    const [, year, month, date] = parts;
    return new Date(Number(year), Number(month) - 1, Number(date));
};

/** The `Date` the picker gives back, as `YYYY-MM-DD`. `null` gives the empty string. */
export const toDayString = (date: Date | null): string =>
    date ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` : '';
